import React, { useRef, useState } from 'react';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Heading, Eye, Pencil } from 'lucide-react';
import { clsx } from 'clsx';
import { MarkdownView } from './MarkdownView';

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  theme?: string;
  minHeight?: number;
  /** When true, render only the textarea with no toolbar — useful when several editors share one toolbar above (not used yet). */
  hideToolbar?: boolean;
}

type ButtonId = 'bold' | 'italic' | 'heading' | 'bullet' | 'numbered' | 'link';

/**
 * Lightweight markdown editor — plain textarea with a toolbar that wraps or
 * prefixes the user's selection. Stores plain markdown text; the matching
 * <MarkdownView> renders it (sanitized) on the public site.
 */
export const MarkdownEditor: React.FC<Props> = ({
  value, onChange, placeholder, theme, minHeight = 160, hideToolbar = false,
}) => {
  const isLight = theme !== 'dark';
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [showPreview, setShowPreview] = useState(false);

  const apply = (id: ButtonId) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end);
    const before = value.slice(0, start);
    const after = value.slice(end);

    let nextValue = value;
    let nextSelStart = start;
    let nextSelEnd = end;

    if (id === 'bold' || id === 'italic') {
      const marker = id === 'bold' ? '**' : '*';
      const placeholderText = id === 'bold' ? 'texto en negrita' : 'texto en cursiva';
      const text = selected || placeholderText;
      nextValue = `${before}${marker}${text}${marker}${after}`;
      nextSelStart = before.length + marker.length;
      nextSelEnd = nextSelStart + text.length;
    } else if (id === 'heading') {
      // Prefix the current line with "## ". Avoid double-prefixing.
      const lineStart = before.lastIndexOf('\n') + 1;
      const lineBefore = value.slice(0, lineStart);
      const restOfLine = value.slice(lineStart);
      const cleaned = restOfLine.replace(/^#{1,6}\s+/, '');
      nextValue = `${lineBefore}## ${cleaned}`;
      const cursor = lineBefore.length + 3 + (start - lineStart - (restOfLine.length - cleaned.length));
      nextSelStart = nextSelEnd = Math.max(cursor, lineBefore.length + 3);
    } else if (id === 'bullet' || id === 'numbered') {
      // Prefix every line in the selection. If nothing selected, prefix current line.
      const blockStart = before.lastIndexOf('\n') + 1;
      const blockEnd = end + Math.max(0, after.indexOf('\n') < 0 ? after.length : after.indexOf('\n'));
      const block = value.slice(blockStart, blockEnd);
      const lines = block.split('\n');
      const prefixed = lines.map((line, i) => {
        if (line.trim() === '') return line;
        const stripped = line.replace(/^(\s*([-*+]|\d+\.)\s+)/, '');
        return id === 'bullet' ? `- ${stripped}` : `${i + 1}. ${stripped}`;
      }).join('\n');
      nextValue = `${value.slice(0, blockStart)}${prefixed}${value.slice(blockEnd)}`;
      nextSelStart = nextSelEnd = blockStart + prefixed.length;
    } else if (id === 'link') {
      const url = window.prompt('URL del enlace', 'https://');
      if (!url) return;
      const text = selected || 'texto del enlace';
      nextValue = `${before}[${text}](${url})${after}`;
      nextSelStart = before.length + 1;
      nextSelEnd = nextSelStart + text.length;
    }

    onChange(nextValue);
    // Restore selection on the next tick — the value change re-renders the textarea.
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(nextSelStart, nextSelEnd);
    });
  };

  const inputCls = clsx(
    'w-full px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/40 resize-y font-mono',
    isLight ? 'bg-white text-slate-900' : 'bg-white/5 text-slate-100',
  );

  const wrapperCls = clsx(
    'rounded-xl border overflow-hidden',
    isLight ? 'bg-white border-slate-200' : 'bg-white/5 border-white/10'
  );

  const btnCls = (active = false) => clsx(
    'p-2 rounded-lg transition-colors',
    active
      ? (isLight ? 'bg-slate-900 text-white' : 'bg-white text-slate-900')
      : (isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-400 hover:bg-white/10')
  );

  const TOOLBAR: { id: ButtonId; label: string; Icon: React.ComponentType<{ size?: number }> }[] = [
    { id: 'bold',     label: 'Negrita',         Icon: Bold },
    { id: 'italic',   label: 'Cursiva',         Icon: Italic },
    { id: 'heading',  label: 'Subtítulo',       Icon: Heading },
    { id: 'bullet',   label: 'Lista con viñetas', Icon: List },
    { id: 'numbered', label: 'Lista numerada',  Icon: ListOrdered },
    { id: 'link',     label: 'Enlace',          Icon: LinkIcon },
  ];

  return (
    <div className={wrapperCls}>
      {!hideToolbar && (
        <div className={clsx(
          'flex items-center justify-between gap-1 px-2 py-1.5 border-b',
          isLight ? 'border-slate-200 bg-slate-50' : 'border-white/10 bg-white/[0.02]'
        )}>
          <div className="flex items-center gap-0.5 flex-wrap">
            {TOOLBAR.map(b => (
              <button
                key={b.id}
                type="button"
                onClick={() => apply(b.id)}
                title={b.label}
                aria-label={b.label}
                className={btnCls()}
              >
                <b.Icon size={15} />
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowPreview(p => !p)}
            title={showPreview ? 'Editar' : 'Vista previa'}
            aria-label={showPreview ? 'Editar' : 'Vista previa'}
            className={btnCls(showPreview)}
          >
            {showPreview ? <Pencil size={15} /> : <Eye size={15} />}
          </button>
        </div>
      )}

      {showPreview ? (
        <div className={clsx('p-4', isLight ? 'bg-white' : 'bg-white/[0.02]')} style={{ minHeight }}>
          {value.trim()
            ? <MarkdownView value={value} className="text-slate-700 dark:text-slate-200" />
            : <p className="text-sm text-slate-400 italic">Sin contenido. Escribe algo para verlo aquí.</p>}
        </div>
      ) : (
        <textarea
          ref={taRef}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputCls}
          style={{ minHeight }}
        />
      )}
    </div>
  );
};
